import { t } from "i18next";
import { useState } from "react";
import { Button, Form } from "react-bootstrap";
import { VideoTag } from "../../interfaces/video";
import { createVideoTag, updateVideoTag, deleteVideoTag } from "../../services/video";

interface VideoTagPickerProps {
    tags: VideoTag[];
    selectedTagIds: number[];
    onChange: (selectedIds: number[]) => void;
    allowCreate?: boolean;
    allowManage?: boolean;
    onTagCreated?: (tag: VideoTag) => void;
    onTagUpdated?: (tag: VideoTag) => void;
    onTagDeleted?: (tagId: number) => void;
}

export function VideoTagPicker({ tags, selectedTagIds, onChange, allowCreate = false, allowManage = false, onTagCreated, onTagUpdated, onTagDeleted }: VideoTagPickerProps) {
    const [search, setSearch] = useState('');
    const [newTagName, setNewTagName] = useState('');
    const [editingTagId, setEditingTagId] = useState<number | null>(null);
    const [editingTagName, setEditingTagName] = useState('');

    const filteredTags = search
        ? tags.filter(tag => tag.name.toLowerCase().includes(search.toLowerCase()))
        : tags;

    const handleToggle = (tagId: number) => {
        if (selectedTagIds.includes(tagId)) {
            onChange(selectedTagIds.filter(id => id !== tagId));
        } else {
            onChange([...selectedTagIds, tagId]);
        }
    };

    const handleCreateTag = () => {
        const name = newTagName.trim();
        if (!name) return;
        createVideoTag(name)
            .then(response => {
                if (response.data.status == 1) {
                    const newTag = response.data.data;
                    if (onTagCreated) onTagCreated(newTag);
                    onChange([...selectedTagIds, newTag.id]);
                    setNewTagName('');
                } else {
                    alert(t("Could not create tag. It may already exist."));
                }
            })
            .catch(() => {
                alert(t("Error creating tag. Please try again."));
            });
    };

    const handleStartEdit = (tag: VideoTag) => {
        setEditingTagId(tag.id);
        setEditingTagName(tag.name);
    };

    const handleSaveEdit = () => {
        if (!editingTagId) return;
        const name = editingTagName.trim();
        if (!name) return;
        updateVideoTag(editingTagId, name)
            .then(response => {
                if (response.data.status == 1) {
                    if (onTagUpdated) onTagUpdated(response.data.data);
                    setEditingTagId(null);
                    setEditingTagName('');
                } else {
                    alert(t("Could not rename tag. The name may already exist."));
                }
            })
            .catch(() => {
                alert(t("Error updating tag. Please try again."));
            });
    };

    const handleCancelEdit = () => {
        setEditingTagId(null);
        setEditingTagName('');
    };

    const handleDeleteTag = (tag: VideoTag) => {
        if (!window.confirm(t("Are you sure you want to delete the tag \"{{name}}\"?", { name: tag.name }))) return;
        deleteVideoTag(tag.id)
            .then(() => {
                if (onTagDeleted) onTagDeleted(tag.id);
                onChange(selectedTagIds.filter(id => id !== tag.id));
            })
            .catch(() => {
                alert(t("Error deleting tag. Please try again."));
            });
    };

    return (
        <div className="video-tag-picker">
            <Form.Control
                size="sm"
                type="text"
                placeholder={t("Search tags")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="mb-2"
            />
            <div className="video-tag-picker-list">
                {filteredTags.map(tag => (
                    <div key={tag.id} className="video-tag-picker-item">
                        {editingTagId === tag.id ? (
                            <div className="d-flex align-items-center gap-1 w-100">
                                <Form.Control
                                    size="sm"
                                    type="text"
                                    value={editingTagName}
                                    onChange={(e) => setEditingTagName(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') { e.preventDefault(); handleSaveEdit(); }
                                        if (e.key === 'Escape') handleCancelEdit();
                                    }}
                                    autoFocus
                                />
                                <Button size="sm" variant="outline-success" onClick={handleSaveEdit} title={t("Save")}>
                                    <i className="bi bi-check"></i>
                                </Button>
                                <Button size="sm" variant="outline-secondary" onClick={handleCancelEdit} title={t("Cancel")}>
                                    <i className="bi bi-x"></i>
                                </Button>
                            </div>
                        ) : (
                            <>
                                <Form.Check
                                    type="checkbox"
                                    id={`tag-${tag.id}`}
                                    label={tag.name}
                                    checked={selectedTagIds.includes(tag.id)}
                                    onChange={() => handleToggle(tag.id)}
                                />
                                {allowManage &&
                                    <span className="video-tag-picker-actions">
                                        <i className="bi bi-pencil" role="button" title={t("Edit")} onClick={() => handleStartEdit(tag)}></i>
                                        <i className="bi bi-trash" role="button" title={t("Delete")} onClick={() => handleDeleteTag(tag)}></i>
                                    </span>
                                }
                            </>
                        )}
                    </div>
                ))}
                {filteredTags.length === 0 &&
                    <div className="text-muted small">{t("No tags found")}</div>
                }
            </div>
            {allowCreate &&
                <div className="d-flex gap-2 mt-2">
                    <Form.Control
                        size="sm"
                        type="text"
                        placeholder={t("New tag")}
                        value={newTagName}
                        onChange={(e) => setNewTagName(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleCreateTag(); } }}
                    />
                    <Button size="sm" variant="outline-secondary" onClick={handleCreateTag} disabled={!newTagName.trim()}>
                        <i className="bi bi-plus"></i>
                    </Button>
                </div>
            }
        </div>
    );
}
